export const SATELLITE_PRODUCTS = [
  "NDVI", "NDWI", "GNDVI", "NDRE", "NDMI", "crop_performance", "current_stress",
] as const;

export type IndexName = typeof SATELLITE_PRODUCTS[number];

export const PRODUCT_LABELS: Record<IndexName, string> = {
  NDVI: "NDVI",
  NDWI: "NDWI",
  GNDVI: "GNDVI",
  NDRE: "NDRE",
  NDMI: "NDMI",
  crop_performance: "Crop performance",
  current_stress: "Current stress",
};

export const PRODUCT_DESCRIPTIONS: Record<IndexName, string> = {
  NDVI: "Vegetation greenness and density.",
  NDWI: "Surface water and very wet areas.",
  GNDVI: "Chlorophyll content, more sensitive in dense crops than NDVI.",
  NDRE: "Chlorophyll and nitrogen status using the red-edge band.",
  NDMI: "Moisture in the crop canopy.",
  crop_performance: "Overall crop condition over the last 14 days (0–100, higher is better).",
  current_stress: "Water, chlorophyll and vegetation stress right now (0–100, higher is worse).",
};

export const defaultDisplayRange = (name: IndexName): [number, number] =>
  name === "NDVI" ? [0, 0.8] : defaultRange(name);

export type RGBA = [number, number, number, number];
type RGB = [number, number, number];
const clamp01 = (x: number) => (x < 0 ? 0 : x > 1 ? 1 : x);
const mix = (a: RGB, b: RGB, t: number): RGB =>
  [0, 1, 2].map((i) => Math.round(a[i] + (b[i] - a[i]) * t)) as RGB;

// Red (poor) -> yellow -> green (good).
const goodBadColor = (t: number): RGBA => {
  const tt = clamp01(Number.isFinite(t) ? t : 0);
  const rgb = tt < 0.5 ? mix([255, 0, 0], [255, 255, 0], tt * 2) : mix([255, 255, 0], [0, 180, 0], (tt - 0.5) * 2);
  return [...rgb, 220];
};

// Brown (dry, below zero) -> white (zero) -> teal (wet).
const wetnessColor = (v: number, min: number, max: number): RGBA => {
  if (v >= 0) return [...mix([245, 245, 245], [1, 102, 94], clamp01(v / Math.max(1e-6, max))), 220];
  return [...mix([140, 81, 10], [245, 245, 245], clamp01((v - min) / Math.max(1e-6, -min))), 220];
};

/** Colour for a pixel value, given the displayed value range. */
export const satelliteColor = (name: IndexName, v: number, min: number, max: number): RGBA => {
  if (name === "NDWI" || name === "NDMI") return wetnessColor(v, min, max);
  const t = clamp01((v - min) / Math.max(1e-6, max - min));
  return goodBadColor(name === "current_stress" ? 1 - t : t);
};

/** CSS gradient matching satelliteColor across the displayed range. */
export const satelliteGradient = (name: IndexName, [min, max]: [number, number]) => {
  const stops = Array.from({ length: 11 }, (_, i) => {
    const [r, g, b] = satelliteColor(name, min + ((max - min) * i) / 10, min, max);
    return `rgb(${r}, ${g}, ${b}) ${i * 10}%`;
  });
  return `linear-gradient(to right, ${stops.join(", ")})`;
};

export const isScore = (name: IndexName) =>
  name === "crop_performance" || name === "current_stress";

export const defaultRange = (name: IndexName): [number, number] =>
  isScore(name) ? [0, 100] : [-1, 1];

export const isValidValue = (value: number, name: IndexName, noData?: number) =>
  Number.isFinite(value) &&
  value !== noData &&
  value !== -9999 &&
  value !== 32767 &&
  value !== 65535 &&
  (isScore(name) ? value >= 0 && value <= 100 : value >= -1.5 && value <= 1.5);

export const observationDate = (entry: {
  acquisition_date?: string | null;
  ingestion_date?: string | null;
  created_at: string;
}) => entry.acquisition_date || entry.ingestion_date || entry.created_at;

// Acquisition dates are UTC calendar days, so format them in UTC to avoid shifting the day.
export const observationDay = (entry: {
  acquisition_date?: string | null;
  ingestion_date?: string | null;
  created_at: string;
}) => new Date(observationDate(entry)).toLocaleDateString(undefined, {
  year: "numeric", month: "short", day: "numeric",
  ...(entry.acquisition_date ? { timeZone: "UTC" } : {}),
});
